import { useContext, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Button } from './Button'
import { ScreenGuideHost } from '../ScreenGuide'
import { usePushBackHandler } from '../../context/BackStackContext'
import { TourGuideContext } from '../tourguide/tourGuideContextCore'

const useBodyPortalTarget = (): HTMLElement | null => {
  const [target] = useState<HTMLElement | null>(() => (typeof document === 'undefined' ? null : document.body))
  return target
}

type MobileFullScreenPanelProps = {
  title: string
  eyebrow?: string
  screenGuideId?: string
  uiName?: string
  className?: string
  headerClassName?: string
  titleClassName?: string
  contentClassName?: string
  closeLabel?: string
  onClose: () => void
  children: ReactNode
  layer?: 'app' | 'dialog'
}

/**
 * Full-screen overlay for the mobile shells. Uses an opaque backdrop layer so
 * whatever renders behind (Board, Todos, Chat, etc.) never bleeds through the
 * translucent panels used inside — the ambient aurora sheen sits on top of a
 * solid slate-950 base for the premium frosted-glass feel across surfaces.
 */
export const MobileFullScreenPanel = ({
  title,
  eyebrow,
  screenGuideId,
  uiName,
  className,
  headerClassName,
  titleClassName,
  contentClassName,
  closeLabel = 'Close',
  onClose,
  children,
  layer = 'app',
}: MobileFullScreenPanelProps) => {
  usePushBackHandler(onClose)
  const tourGuide = useContext(TourGuideContext)
  const isTourActive = Boolean(tourGuide?.isTourActive)

  const isDialogLayer = layer === 'dialog'
  const portalTarget = useBodyPortalTarget()

  const panel = (
    <div
      className={`mobileFullScreenPanel fixed inset-0 ${isDialogLayer ? 'mobileFullScreenPanel--dialog ' : 'z-[120] '}isolate text-[var(--text-strong)] overflow-y-auto overscroll-contain ${className ?? ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-ui-name={uiName}
      data-screen-guide-id={screenGuideId}
      style={{
        backgroundColor: 'var(--bg)',
        ...(isDialogLayer ? { zIndex: 'var(--z-dialog)' } : {}),
      }}
    >
      <div
        className="mobileFullScreenPanel__backdrop fixed inset-0 pointer-events-none"
        style={{ backgroundColor: 'var(--bg)', zIndex: -1 }}
        aria-hidden="true"
      />
      <div className="mobileFullScreenPanel__aurora pointer-events-none absolute inset-0" aria-hidden="true" />
    <header
      className={`mobileFullScreenPanel__header sticky top-0 z-10 flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--border)] backdrop-blur-2xl ${headerClassName ?? ''}`}
      style={{
        paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
        backgroundColor: 'color-mix(in srgb, var(--bg) 92%, transparent)',
      }}
    >
      <div className="min-w-0">
        {eyebrow ? (
          <span className="text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">{eyebrow}</span>
        ) : null}
        <h2 className={`mobileFullScreenPanel__title text-sm font-semibold text-[var(--text-strong)] ${titleClassName ?? ''}`}>{title}</h2>
      </div>
      <div className="flex items-center gap-2">
        {screenGuideId ? (
          <ScreenGuideHost screenId={screenGuideId} disabled={isTourActive} className="screenGuideLauncher--topbar" />
        ) : null}
        <Button
          variant="tertiary"
          size="md"
          icon={<CloseRoundedIcon fontSize="inherit" aria-hidden="true" />}
          onClick={onClose}
          className="shrink-0"
          aria-label={closeLabel}
        >
          {closeLabel}
        </Button>
      </div>
    </header>
    <main
      className={`mobileFullScreenPanel__content relative px-4 py-4 ${contentClassName ?? ''}`}
      style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))' }}
    >
      {children}
    </main>
  </div>
  )

  if (isDialogLayer && portalTarget) {
    return createPortal(panel, portalTarget)
  }

  return panel
}
